import { DOCUMENT } from '@angular/common';
import { Injectable, inject } from '@angular/core';
import {
  OrganizationBranding,
  OrganizationTheme,
  OrganizationThemePalette,
  ThemeMode
} from '../models/organization-branding.model';
import { Organization } from '../../features/organizations/models/organization.model';
import {
  DEFAULT_LIGHT_PALETTE_CODE,
  LightPaletteCode,
  resolveLightPalette
} from '../theme/light-palettes';

@Injectable({
  providedIn: 'root'
})
export class ThemeService {
  private readonly document = inject(DOCUMENT);
  private readonly storageKey = 'app.theme.mode';
  private readonly lightPaletteStorageKey = 'app.theme.lightPalette';
  private readonly organizationThemeCssVariables: ReadonlyArray<string> = [
    '--brand-primary',
    '--brand-secondary',
    '--layout-background',
    '--layout-surface',
    '--layout-border',
    '--layout-text',
    '--layout-text-muted',
    '--sidebar-background',
    '--sidebar-border',
    '--sidebar-text',
    '--sidebar-text-muted'
  ];

  private selectedLightPaletteCode: LightPaletteCode = DEFAULT_LIGHT_PALETTE_CODE;

  initializeTheme(branding: OrganizationBranding): OrganizationBranding {
    this.selectedLightPaletteCode = this.resolveStoredLightPaletteCode();

    const storedMode = this.getStoredMode();
    const resolvedMode = storedMode ?? branding.theme.mode;
    const theme = this.withMode(branding.theme, resolvedMode);

    this.applyTheme(theme);

    return {
      ...branding,
      theme
    };
  }

  setMode(theme: OrganizationTheme, mode: ThemeMode): OrganizationTheme {
    const nextTheme = this.withMode(theme, mode);
    this.applyTheme(nextTheme);
    this.persistMode(mode);

    return nextTheme;
  }

  applyOrganizationTheme(organization: Organization | null): void {
    void organization;
  }

  /**
   * Applies a mode visually WITHOUT persisting the user's preference. Public
   * unauthenticated routes (login, pricing) use it to force a light appearance
   * regardless of the stored dark-mode preference.
   */
  applyTransientMode(theme: OrganizationTheme, mode: ThemeMode): void {
    this.applyTheme(this.withMode(theme, mode));
  }

  /**
   * Re-applies the persisted mode after a transient one (when leaving a
   * forced-light route). Reads storage so a preference saved while the
   * transient mode was active (e.g. right after login) is honored.
   */
  restorePersistedMode(theme: OrganizationTheme): void {
    this.applyTheme(this.withMode(theme, this.getStoredMode() ?? theme.mode));
  }

  setLightPalette(paletteCode: string | null | undefined): LightPaletteCode {
    const palette = resolveLightPalette(paletteCode);
    this.selectedLightPaletteCode = palette.code;
    this.persistLightPaletteCode(palette.code);

    if (this.resolveCurrentThemeMode() === 'light') {
      const rootElement = this.document.documentElement;
      this.clearOrganizationThemeCssVariables(rootElement);
      this.applyLightPaletteVariables(rootElement);
    }

    return palette.code;
  }

  getLightPaletteCode(): LightPaletteCode {
    return this.selectedLightPaletteCode;
  }

  private withMode(theme: OrganizationTheme, mode: ThemeMode): OrganizationTheme {
    return {
      ...theme,
      mode
    };
  }

  private applyTheme(theme: OrganizationTheme): void {
    const rootElement = this.document.documentElement;
    const bodyElement = this.document.body;
    const palette = theme[theme.mode];
    const isDarkMode = theme.mode === 'dark';

    rootElement.setAttribute('data-theme', theme.mode);
    rootElement.classList.toggle('dark', isDarkMode);
    bodyElement?.classList.toggle('dark', isDarkMode);
    bodyElement?.classList.toggle('theme-dark', isDarkMode);
    bodyElement?.classList.toggle('theme-light', !isDarkMode);

    if (isDarkMode) {
      this.setCssVariables(rootElement, palette);
      return;
    }

    this.clearOrganizationThemeCssVariables(rootElement);
    this.applyLightPaletteVariables(rootElement);
  }

  private setCssVariables(rootElement: HTMLElement, palette: OrganizationThemePalette): void {
    rootElement.style.setProperty('--brand-primary', palette.primaryColor);
    rootElement.style.setProperty('--brand-secondary', palette.secondaryColor);
    rootElement.style.setProperty('--layout-background', palette.backgroundColor);
    rootElement.style.setProperty('--layout-surface', palette.surfaceColor);
    rootElement.style.setProperty('--layout-border', palette.borderColor);
    rootElement.style.setProperty('--layout-text', palette.textColor);
    rootElement.style.setProperty('--layout-text-muted', palette.textMutedColor);
    rootElement.style.setProperty('--sidebar-background', palette.sidebarBackgroundColor);
    rootElement.style.setProperty('--sidebar-border', palette.sidebarBorderColor);
    rootElement.style.setProperty('--sidebar-text', palette.sidebarTextColor);
    rootElement.style.setProperty('--sidebar-text-muted', palette.sidebarTextMutedColor);
  }

  private applyLightPaletteVariables(rootElement: HTMLElement): void {
    const palette = resolveLightPalette(this.selectedLightPaletteCode);

    for (const [token, value] of Object.entries(palette.tokens)) {
      rootElement.style.setProperty(token, value);
    }
  }

  private clearOrganizationThemeCssVariables(rootElement: HTMLElement): void {
    for (const cssVariable of this.organizationThemeCssVariables) {
      rootElement.style.removeProperty(cssVariable);
    }
  }

  private getStoredMode(): ThemeMode | null {
    const storedMode = localStorage.getItem(this.storageKey);

    return storedMode === 'dark' || storedMode === 'light' ? storedMode : null;
  }

  private persistMode(mode: ThemeMode): void {
    localStorage.setItem(this.storageKey, mode);
  }

  private resolveStoredLightPaletteCode(): LightPaletteCode {
    const storedPaletteCode = localStorage.getItem(this.lightPaletteStorageKey);
    return resolveLightPalette(storedPaletteCode).code;
  }

  private persistLightPaletteCode(paletteCode: LightPaletteCode): void {
    localStorage.setItem(this.lightPaletteStorageKey, paletteCode);
  }

  private resolveCurrentThemeMode(): ThemeMode {
    return this.document.documentElement.getAttribute('data-theme') === 'dark' ? 'dark' : 'light';
  }
}
